/**
 * Service for image processing operations including HEIC conversion and file handling.
 */

import heic2any from 'heic2any';

/**
 * Detects if a file is in HEIC format by checking file extension and MIME type.
 * 
 * @param file - The file to check
 * @returns true if the file appears to be HEIC format
 */
export function isHeicFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();
  
  // Check file extension
  const heicExtensions = ['.heic', '.heif', '.hif'];
  const hasHeicExtension = heicExtensions.some(ext => fileName.endsWith(ext));
  
  // Check MIME type
  const heicMimeTypes = ['image/heic', 'image/heif', 'image/hif'];
  const hasHeicMimeType = heicMimeTypes.includes(fileType);
  
  return hasHeicExtension || hasHeicMimeType;
}

/**
 * Detects HEIC format by reading the file header (magic bytes).
 * Useful for files without extensions or incorrect MIME types.
 * 
 * @param file - The file to check
 * @returns Promise<boolean> - true if the file header indicates HEIC format
 */
export async function detectHeicByHeader(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      if (!arrayBuffer || arrayBuffer.byteLength < 12) {
        resolve(false);
        return;
      }
      
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // HEIC files start with specific magic bytes
      // ftyp box at offset 4, followed by 'heic', 'heif', 'mif1', or 'msf1'
      if (uint8Array.length >= 12) {
        const ftyp = String.fromCharCode(
          uint8Array[4], uint8Array[5], uint8Array[6], uint8Array[7]
        );
        
        if (ftyp === 'ftyp') {
          const brand = String.fromCharCode(
            uint8Array[8], uint8Array[9], uint8Array[10], uint8Array[11]
          );
          const heicBrands = ['heic', 'heif', 'mif1', 'msf1'];
          resolve(heicBrands.includes(brand));
          return;
        }
      }
      resolve(false);
    };
    reader.onerror = () => resolve(false);
    // Only read first 12 bytes to check magic number
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
}

/**
 * Converts a HEIC file to JPEG format using heic2any library.
 * 
 * @param file - The HEIC file to convert
 * @param quality - JPEG quality (0-1), default 0.92
 * @returns Promise<File> - The converted JPEG file
 * @throws Error if conversion fails
 */
export async function convertHeicToJpeg(file: File, quality: number = 0.92): Promise<File> {
  try {
    const convertedBlob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: quality
    });
    
    // heic2any returns an array, get the first item
    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    
    // Create a new File object from the converted blob
    // Replace HEIC extension if present, otherwise append .jpg
    let newName: string;
    if (/\.(heic|heif|hif)$/i.test(file.name)) {
      // File has HEIC extension, replace it
      newName = file.name.replace(/\.(heic|heif|hif)$/i, '.jpg');
    } else {
      // File has no extension or different extension, append .jpg
      newName = `${file.name}.jpg`;
    }
    
    const convertedFile = new File(
      [blob],
      newName,
      { type: 'image/jpeg' }
    );
    
    return convertedFile;
  } catch (error) {
    throw new Error('Failed to convert HEIC file to JPEG');
  }
}

/**
 * Converts a file to base64 string.
 * 
 * @param file - The file to convert
 * @returns Promise<string> - The base64 encoded string (without data URL prefix)
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
}

/**
 * Processes an image file, converting HEIC to JPEG if necessary.
 * 
 * @param file - The file to process
 * @returns Promise<{ file: File; previewFile: File }> - The processed file and preview file
 */
export async function processImageFile(file: File): Promise<{ file: File; previewFile: File }> {
  let fileToProcess = file;
  let previewFile = file;
  
  // Check if file is HEIC and convert to JPEG if needed
  let isHeic = isHeicFile(file);
  
  // If not detected by extension/MIME type, try detecting by file header
  // Always check header if file has no extension (most reliable method for files without extensions)
  const hasExtension = file.name.includes('.');
  const hasMimeType = !!file.type;
  
  // Always check header if no extension (regardless of MIME type or initial detection)
  // This is critical for files without extensions, as MIME types can be unreliable
  if (!hasExtension) {
    const headerDetectionResult = await detectHeicByHeader(file);
    if (headerDetectionResult) {
      isHeic = true;
    }
  } else if (!isHeic && !hasMimeType) {
    // If file has extension but no MIME type and wasn't detected, check header as fallback
    const headerDetectionResult = await detectHeicByHeader(file);
    if (headerDetectionResult) {
      isHeic = true;
    }
  }
  
  if (isHeic) {
    fileToProcess = await convertHeicToJpeg(file);
    previewFile = fileToProcess; // Use converted file for preview
  }
  
  return { file: fileToProcess, previewFile };
}

